import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import Grid from '@mui/material/Grid';

import UploadIcon from '@mui/icons-material/CloudUpload';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';
import { createStdRequest, apiFetch, sortedDropdownItemLabels } from '../utils/utils';

import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';
import KSelect from './KSelect';

const getColor = (props) => {
  if (props.isDragAccept) {
    return '#00e676';
  }
  if (props.isDragReject) {
    return '#ff1744';
  }
  if (props.isFocused) {
    return '#2196f3';
  }
  return '#eeeeee';
};

const DropContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 3px;
  border-radius: 2px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: black;
  outline: none;
  transition: border 0.24s ease-in-out;
  margin-top: 12px;
`;

const UploadIconDiv = styled.div`
  scale: 1.6;
  margin-top: 12px;
`;

const maxFileSize = 10 * 1024 * 1024; // 10 MB

function fileSizeValidator(file) {
  if (file.size > maxFileSize) {
    return {
      code: 'file-too-large',
      message: `File is larger than 10 MB`,
    };
  }
  return null;
}

function NewDocsForm(props) {
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [documentFiles, setDocumentFiles] = useState([]);
  const [rejectedFiles, setRejectedFiles] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      setDocumentFiles([...documentFiles, ...acceptedFiles]);
      setRejectedFiles([...rejectedFiles, ...fileRejections]);
    },
    [documentFiles, rejectedFiles]
  );

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/png': ['.gif'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    validator: fileSizeValidator,
  });

  const documentTypeOptions = ['None', ...sortedDropdownItemLabels('Document Type')];

  const acceptForm = () => {
    const personId = props.clientId;
    setDescription(description.trim());

    if (personId > 0) {
      const uri = `/api/document/viewingFirm/${DA.getFirmId()}/person/${personId}`;
      for (let i = 0; i < documentFiles.length; i++) {
        const formData = new FormData();
        formData.append('file', documentFiles[i]);

        fetch(uri, { method: 'POST', body: formData })
          .then((response) => response.json())
          .then(async (result) => {
            if (await result) {
              addNewDocumentToReduxStore(result);
              if (description !== '' || (documentType !== '' && documentType !== 'None')) {
                updateDocumentRequest(result.storedDocumentId);
                clearForm();
              }
              props.closeModalFunc();
            }
          })
          .catch((error) => console.log(error));
      }
    } else {
      let docsList = props.parent.state.docs;
      for (let i = 0; i < documentFiles.length; i++) {
        const doc = {
          storedDocumentId: 0,
          personId: 0,
          inquiryId: props.inquiryId,
          publicName: documentFiles[i].name,
          typeTag: documentType,
          description: description,
          data: documentFiles[i],
        };
        docsList = [...docsList, doc];
      }
      props.parent.setState({ docs: docsList, isDocModified: true });
      clearForm();
      props.closeModalFunc();
    }
  };

  const clearForm = () => {
    setDescription('');
    setDocumentType('');
    setDocumentFiles([]);
    setRejectedFiles([]);
  };

  const cancelModal = () => {
    clearForm();
    props.closeModalFunc();
  };

  const addNewDocumentToReduxStore = (doc) => {
    ReduxLoader.addDocuments([doc]);
  };

  const updateDocumentRequest = (documentId) => {
    const uri = `/api/document/${documentId}`;
    const apiStruct = {
      typeTag: documentType === 'None' ? null : documentType,
      description: description,
    };
    const request = createStdRequest(uri, 'put', apiStruct);
    const actionOnGoodResponse = (json) => {
      ReduxLoader.updateDocument(json);
    };
    apiFetch(null, request, actionOnGoodResponse);
  };

  const fileListItems = documentFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));

  const fileRejectionItems = rejectedFiles.map(({ file, errors }) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ));

  return (
    <div>
      <Grid item xs={12}>
        <CrmTextField
          label="Description"
          fullWidth
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Grid>
      {documentTypeOptions.length > 0 && (
        <Grid item style={{ zIndex: 2 }} xs={12}>
          <KSelect
            fullWidth
            style={{ minWidth: '230px' }}
            label="Document Type (optional)"
            liveOptions={documentTypeOptions}
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value)}
          />
        </Grid>
      )}

      <section style={{ marginTop: '10px' }}>
        <DropContainer {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
          <input {...getInputProps()} />
          <p>Drag 'n' drop some files here, or click to select files</p>
          <UploadIconDiv>
            <UploadIcon />
          </UploadIconDiv>
        </DropContainer>
        <aside>
          {fileListItems.length > 0 && (
            <div>
              <h4>Accepted Files</h4>
              <ul>{fileListItems}</ul>
            </div>
          )}
          {fileRejectionItems.length > 0 && (
            <div>
              <h4>Rejected Files</h4>
              <ul>{fileRejectionItems}</ul>
            </div>
          )}
        </aside>
      </section>

      <Grid item xs={12}>
        <hr />
      </Grid>

      <Grid item xs={12}>
        <div style={{ float: 'right' }}>
          <CrmButton outlined onClick={() => cancelModal()}>
            Cancel
          </CrmButton>
          <CrmButton outlined onClick={() => clearForm()}>
            Clear
          </CrmButton>
          <CrmButton filled onClick={() => acceptForm()}>
            Save
          </CrmButton>
        </div>
      </Grid>
    </div>
  );
}

export default NewDocsForm;
